import { APP_VERSION } from "@/config/appConfig"
import { getStoredVersion, isNewVersion, setStoredVersion } from "@/utils/versionManager"
import type { Logger } from "@/lib/logging"

const PWA_DEBUG_SESSION_ID = "pwa-non-precached"

function reportPwaDebug(hypothesisId: string, location: string, msg: string, data: Record<string, unknown>) {
  if (!(import.meta as any)?.env?.DEV) return
  if (typeof window === "undefined" || (window as any).__TRAE_PWA_DEBUG__ !== true) return
  const pwaDebugUrl = String((window as any).__TRAE_LOCAL_DEBUG_URL__ || "").trim()
  if (!pwaDebugUrl) return
  fetch(pwaDebugUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      sessionId: PWA_DEBUG_SESSION_ID,
      runId: "pre-fix",
      hypothesisId,
      location,
      msg,
      data,
      ts: Date.now(),
    }),
  }).catch(() => { })
}

type UpdateReason = "service-worker" | "version"

type UpdateState = {
  updateAvailable: boolean
  reason: UpdateReason
  currentVersion: string
  storedVersion: string | null
}

let started = false
let updateAvailable = false
let updateReason: UpdateReason = "service-worker"
let reloadScheduled = false

function isNetworkOnlyMode(): boolean {
  try {
    const until = Number(localStorage.getItem("pwa_network_only_until") || "0")
    return Number.isFinite(until) && until > Date.now()
  } catch {
    return false
  }
}

function getSessionUpdatedFlag(): boolean {
  try {
    return sessionStorage.getItem("app_updated") === "true"
  } catch {
    return false
  }
}

function setSessionUpdatedFlag(): void {
  try {
    sessionStorage.setItem("app_updated", "true")
  } catch {
  }
}

function snapshotState(reason: UpdateReason): UpdateState {
  return {
    updateAvailable,
    reason,
    currentVersion: APP_VERSION,
    storedVersion: getStoredVersion(),
  }
}

function publishState(reason: UpdateReason) {
  try {
    const state = snapshotState(reason)
    window.dispatchEvent(new CustomEvent("app:update", { detail: state }))
  } catch {
  }
}

function triggerReload(logger?: Logger) {
  if (reloadScheduled) return
  if (isNetworkOnlyMode()) {
    // #region debug-point D:pwa-reload-suppressed-network-only
    reportPwaDebug("D", "src/services/updateManager.ts:83", "[DEBUG] Update reload suppressed by network-only mode", {
      version: APP_VERSION,
      reason: updateReason,
    })
    // #endregion
    logger?.warning("Update reload suppressed (network-only mode)", {
      category: "app",
      meta: { version: APP_VERSION, reason: updateReason },
    })
    return
  }
  if (getSessionUpdatedFlag()) {
    // #region debug-point D:pwa-reload-suppressed-session
    reportPwaDebug("D", "src/services/updateManager.ts:94", "[DEBUG] Update reload suppressed by session flag", {
      version: APP_VERSION,
      reason: updateReason,
    })
    // #endregion
    logger?.info("Update reload suppressed (already updated once this session)", {
      category: "app",
      meta: { version: APP_VERSION, reason: updateReason },
    })
    return
  }
  reloadScheduled = true
  window.setTimeout(() => {
    if (!updateAvailable) return
    if (getSessionUpdatedFlag()) return
    setSessionUpdatedFlag()
    // #region debug-point B:pwa-trigger-reload
    reportPwaDebug("B", "src/services/updateManager.ts:109", "[DEBUG] Triggering PWA reload", {
      version: APP_VERSION,
      reason: updateReason,
      hasUpdateFn: typeof (window as any).__PWA_UPDATE_SW__ === "function",
    })
    // #endregion
    logger?.info("Reloading app to apply update", { category: "app", meta: { reason: updateReason } })
    const fn = (window as any).__PWA_UPDATE_SW__
    if (typeof fn === "function") {
      fn(true)
      return
    }
    window.location.reload()
  }, 2000)
}

function markUpdateAvailable(reason: UpdateReason, logger?: Logger) {
  if (updateAvailable) return
  updateAvailable = true
  updateReason = reason
  // #region debug-point A:pwa-mark-update
  reportPwaDebug("A", "src/services/updateManager.ts:123", "[DEBUG] Marked update available", {
    reason,
    currentVersion: APP_VERSION,
    storedVersion: getStoredVersion(),
  })
  // #endregion
  publishState(reason)
  logger?.info("Update available", {
    category: "app",
    meta: { reason, stored: getStoredVersion(), current: APP_VERSION },
  })
  if (reason === "service-worker") {
    try {
      navigator.serviceWorker
        ?.getRegistrations?.()
        .then((regs) => Promise.all(regs.map((r) => r.update())))
        .catch(() => {
        })
    } catch {
    }
  }
  triggerReload(logger)
}

export function initUpdateManager(opts?: { logger?: Logger }) {
  if (typeof window === "undefined") return
  if (started) return
  started = true

  const logger = opts?.logger

  const afterReload = getSessionUpdatedFlag()
  if (afterReload) {
    // #region debug-point C:pwa-after-reload
    reportPwaDebug("C", "src/services/updateManager.ts:151", "[DEBUG] Update manager detected post-reload session", {
      version: APP_VERSION,
    })
    // #endregion
    setStoredVersion(APP_VERSION)
    updateAvailable = false
    publishState("version")
  }

  window.addEventListener("pwa-update-available", () => markUpdateAvailable("service-worker", logger))

  const isDifferent = isNewVersion(APP_VERSION)
  if (isDifferent) {
    markUpdateAvailable("version", logger)
    return
  }

  publishState("version")

  window.setTimeout(() => {
    setStoredVersion(APP_VERSION)
    publishState("version")
  }, 0)
}
